/**
 * Sprint 23.5 — HireConversation.
 *
 * The left side of the split-screen wizard: a header strip + a Sprint 22
 * AgentThread that lives in `composition` scope. The runtime is driven by
 * `useCompositionChatRuntime`, which:
 *  - sends `scope: {kind: "composition", target_type: "agent"}`
 *  - sends the live `composition_draft` snapshot on every turn
 *  - intercepts `update_composition_draft` tool calls and forwards each
 *    field/value patch to `onDraftPatch` (consumed by the page).
 *
 * The thread itself is the existing AgentThread component — we get the
 * polished message bubbles, markdown rendering, completion line, streaming
 * spinner, and tool-call card suppression (for composition-draft tools)
 * for free.
 */

import { useEffect, useRef, type CSSProperties } from "react";
import type { UIMessage } from "@ai-sdk/react";
import type { AssistantRuntime } from "@assistant-ui/react";
import { AgentThread } from "../chat/AgentThread";

export interface HireConversationProps {
  title: string;
  runtime: AssistantRuntime;
  chatMessages: UIMessage[];
  status: "submitted" | "streaming" | "ready" | "error";
  error: Error | undefined;
  /** Initial greeting message to seed the chat (sent once on mount). */
  greeting: string;
}

export function HireConversation({
  title,
  runtime,
  chatMessages,
  status,
  error,
  greeting,
}: HireConversationProps) {
  // Seed the greeting as an assistant message exactly once — only if the
  // thread is empty AND we haven't already seeded. Subsequent re-renders
  // (status changes, etc.) leave it alone.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (chatMessages.length > 0) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    try {
      // AgentThread reads chatMessages from the runtime; seeding via the
      // runtime ref keeps it in sync with the AI SDK's internal state.
      // We append a synthetic assistant text message.
      runtime.thread.import?.({
        messages: [
          {
            id: `greet_${Date.now()}`,
            role: "assistant",
            content: [{ type: "text", text: greeting }],
            attachments: [],
            metadata: { unstable_data: {}, unstable_annotations: [], steps: [], custom: {} },
            status: { type: "complete", reason: "stop" },
            createdAt: new Date(0),
          } as never,
        ],
      });
    } catch {
      /* If import isn't supported on this runtime version, the empty-state
       * shows the greeting via AgentThread's emptyState slot below. */
    }
  }, [chatMessages.length, greeting, runtime]);

  return (
    <div style={panel}>
      <header style={headerRow}>
        <i className="ti ti-sparkles" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
        <span style={titleText}>{title}</span>
      </header>
      <div style={threadArea}>
        <AgentThread
          runtime={runtime}
          chatMessages={chatMessages}
          chatStatus={status}
          chatError={error}
          assistantLabel="Kuvio"
          emptyState={
            <div style={empty}>
              <i className="ti ti-message-circle" style={{ fontSize: 22, opacity: 0.5 }} aria-hidden="true" />
              <p style={emptyTitle}>{title}</p>
              <p style={emptySub}>{greeting}</p>
            </div>
          }
        />
      </div>
    </div>
  );
}

const panel: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  background: "var(--color-background-primary)",
  fontFamily: "var(--font-sans)",
  borderRight: "var(--kuvio-border-default)",
};

const headerRow: CSSProperties = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  gap: 8,
  height: 40,
  padding: "0 16px",
  borderBottom: "var(--kuvio-border-default)",
};

const titleText: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
};

const threadArea: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  display: "flex",
  overflow: "hidden",
};

const empty: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  padding: "32px 28px",
  textAlign: "center",
};

const emptyTitle: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: "var(--color-text-primary)",
  margin: 0,
};

const emptySub: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.5,
  color: "var(--color-text-secondary)",
  margin: 0,
  maxWidth: 360,
};
